import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

function FAQ() {
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const breadcrumbs = [
    { label: 'FAQ', path: '/faq' },
  ];

  const faqItems: FAQItem[] = [
    {
      id: 'general-1',
      category: 'General',
      question: 'What is Moreflex Aviation?',
      answer: 'Moreflex Aviation is a premium VIP airport services provider at Jomo Kenyatta International Airport (JKIA) in Nairobi. We specialize in providing exclusive meet-and-greet services, lounge access, chauffeur services, baggage assistance, fast-track security, and comprehensive concierge solutions to enhance your travel experience.',
    },
    {
      id: 'general-2',
      category: 'General',
      question: 'Where are your services available?',
      answer: 'Our primary hub is Jomo Kenyatta International Airport (JKIA) in Nairobi, Kenya. We also coordinate services at other major international airports through our partner network. For inquiries about specific airports, please contact our support team.',
    },
    {
      id: 'general-3',
      category: 'General',
      question: 'Are your services available for both domestic and international flights?',
      answer: 'Yes, we provide comprehensive services for both domestic and international flights at JKIA. Our services are tailored to meet the needs of all travelers, regardless of whether you\'re flying domestically or internationally.',
    },
    {
      id: 'booking-1',
      category: 'Booking & Reservations',
      question: 'How do I book services?',
      answer: 'You can book our services through multiple channels: our website contact form, email, phone, or WhatsApp. We recommend booking at least 24-48 hours in advance to ensure availability. Our team will confirm your reservation and provide detailed instructions.',
    },
    {
      id: 'booking-2',
      category: 'Booking & Reservations',
      question: 'What information do you need to complete a booking?',
      answer: 'We typically require: flight number, departure/arrival date and time, number of passengers, service preferences, any special requirements (disabilities, dietary restrictions, etc.), and contact information. For corporate bookings, we may request additional details.',
    },
    {
      id: 'booking-3',
      category: 'Booking & Reservations',
      question: 'Is there a minimum booking requirement?',
      answer: 'We serve individual travelers and corporate clients of all sizes. While we can accommodate single travelers, some services may have minimum group requirements. Please contact us to discuss your specific needs and requirements.',
    },
    {
      id: 'booking-4',
      category: 'Booking & Reservations',
      question: 'Can I modify or cancel my booking?',
      answer: 'Yes, bookings can be modified or cancelled subject to our cancellation policy. Changes made at least 24 hours in advance typically incur no additional fees. For emergency cancellations or modifications within 24 hours, please contact our support team immediately.',
    },
    {
      id: 'services-1',
      category: 'Services',
      question: 'What is included in the Meet and Greet service?',
      answer: 'Our Meet and Greet service includes: airport arrival pickup by a uniformed representative, assistance with luggage, priority check-in guidance, and customer care throughout your airport experience. Our representative will greet you at your flight\'s arrival point and provide personalized attention.',
    },
    {
      id: 'services-2',
      category: 'Services',
      question: 'What lounges do you provide access to?',
      answer: 'We provide access to premium lounges at JKIA, including partner lounges with amenities such as complimentary beverages, snacks, Wi-Fi, business facilities, comfortable seating, and shower facilities. Lounge availability depends on your flight and airline partnerships.',
    },
    {
      id: 'services-3',
      category: 'Services',
      question: 'Do you offer ground transportation?',
      answer: 'Yes, our Fleet Services include professional chauffeur-driven vehicles. We offer luxury sedans, SUVs, and vehicles suitable for larger groups. All drivers are professionally trained, courteous, and knowledgeable about Nairobi and surrounding areas.',
    },
    {
      id: 'services-4',
      category: 'Services',
      question: 'What does your Concierge service include?',
      answer: 'Our Concierge service is highly customizable and can include: restaurant reservations, event ticketing, business meeting arrangements, tour bookings, emergency assistance, medical referrals, and any other special requests to make your stay exceptional.',
    },
    {
      id: 'services-5',
      category: 'Services',
      question: 'Is Fast-Track security available for all flights?',
      answer: 'Fast-Track security access is available for eligible flights and passengers. Availability depends on your airline, flight status, and current airport procedures. We\'ll confirm eligibility during the booking process and provide alternative solutions if Fast-Track isn\'t available.',
    },
    {
      id: 'pricing-1',
      category: 'Pricing & Payments',
      question: 'How are services priced?',
      answer: 'We offer both à la carte service pricing and customizable packages for corporate clients. Prices vary based on service type, passenger count, timing, and specific requirements. We provide transparent quotes upon request with no hidden fees.',
    },
    {
      id: 'pricing-2',
      category: 'Pricing & Payments',
      question: 'Do you offer corporate or group discounts?',
      answer: 'Yes, we provide special pricing for corporate clients, regular travelers, and group bookings. We\'re committed to building long-term relationships with corporate partners. Please contact us to discuss your organization\'s needs and volume.',
    },
    {
      id: 'pricing-3',
      category: 'Pricing & Payments',
      question: 'What payment methods do you accept?',
      answer: 'We accept multiple payment methods including bank transfers, credit cards (Visa/Mastercard), mobile money (M-Pesa), and wire transfers for international payments. Payment terms can be customized for corporate clients.',
    },
    {
      id: 'pricing-4',
      category: 'Pricing & Payments',
      question: 'Is there an invoice/billing option for corporate accounts?',
      answer: 'Absolutely. We provide invoicing and monthly billing for corporate clients with agreed payment terms. Net-30 and Net-60 terms are available for established corporate accounts. Contact our corporate team to arrange a billing agreement.',
    },
    {
      id: 'accessibility-1',
      category: 'Accessibility & Special Needs',
      question: 'Do you accommodate passengers with disabilities?',
      answer: 'Yes, we fully accommodate passengers with disabilities and provide specialized assistance. Please notify us during booking about any special requirements including mobility aids, visual/hearing assistance, or other accessibility needs.',
    },
    {
      id: 'accessibility-2',
      category: 'Accessibility & Special Needs',
      question: 'Can you assist with medical or dietary requirements?',
      answer: 'We can assist with most dietary restrictions and medical requirements. Please provide details during booking. For specific medical needs, we work with local healthcare providers and can arrange necessary support.',
    },
    {
      id: 'accessibility-3',
      category: 'Accessibility & Special Needs',
      question: 'Do you have services for elderly travelers or families with children?',
      answer: 'Yes, our services are family-friendly and designed to assist elderly travelers and families with children. We can arrange appropriate transportation, accessible facilities, and dedicated assistance tailored to your specific needs.',
    },
    {
      id: 'corporate-1',
      category: 'Corporate & Business',
      question: 'Do you offer business travel packages?',
      answer: 'Yes, we specialize in business travel solutions. We can arrange ground transportation, executive lounge access, business meeting facilities, and comprehensive travel management for your organization\'s employees.',
    },
    {
      id: 'corporate-2',
      category: 'Corporate & Business',
      question: 'Can you manage recurring travel for our staff?',
      answer: 'Absolutely. We work with companies to manage regular travel requirements. We can set up recurring bookings, corporate accounts with preferred pricing, and integrate with your existing travel management systems.',
    },
    {
      id: 'corporate-3',
      category: 'Corporate & Business',
      question: 'Do you provide reporting and expense tracking?',
      answer: 'Yes, we provide detailed reporting for corporate accounts including expense summaries, usage analytics, and itemized invoices for budget tracking and compliance requirements.',
    },
    {
      id: 'quality-1',
      category: 'Quality & Safety',
      question: 'What standards and certifications do you maintain?',
      answer: 'We comply with IATA (International Air Transport Association) standards and maintain certifications for ground handling services. Our team receives regular training in customer service, safety protocols, and professional conduct. We prioritize passenger safety and satisfaction.',
    },
    {
      id: 'quality-2',
      category: 'Quality & Safety',
      question: 'How are your drivers and staff trained?',
      answer: 'All staff members undergo comprehensive training in customer service, safety protocols, emergency procedures, and professional conduct. Our drivers are licensed, background-checked, and regularly updated on the latest customer service standards and airport procedures.',
    },
    {
      id: 'quality-3',
      category: 'Quality & Safety',
      question: 'What is your safety record?',
      answer: 'We maintain an excellent safety record and take all safety protocols seriously. Our vehicles are regularly maintained and inspected. We comply with all applicable laws and international aviation ground handling standards.',
    },
    {
      id: 'quality-4',
      category: 'Quality & Safety',
      question: 'How do you handle passenger privacy and data protection?',
      answer: 'We take data privacy seriously and comply with applicable data protection regulations. All passenger information is treated confidentially and used only for providing our services. We implement appropriate security measures to protect your data.',
    },
  ];

  const categories = ['General', 'Booking & Reservations', 'Services', 'Pricing & Payments', 'Accessibility & Special Needs', 'Corporate & Business', 'Quality & Safety'];

  const FAQSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <div className="font-[Arial] text-black">
      <script type="application/ld+json">{JSON.stringify(FAQSchema)}</script>
      <Header />
      <Breadcrumb items={breadcrumbs} />

      {/* Hero Section */}
      <section className="py-12 bg-gradient-to-r from-[#73BDF2] to-[#5ba8e0] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
            <p className="text-lg opacity-90">
              Find answers to common questions about Moreflex Aviation services, booking, pricing, and more.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {categories.map((category) => {
              const categoryItems = faqItems.filter(item => item.category === category);
              return (
                <div key={category} className="mb-12">
                  <h2 className="text-2xl font-bold mb-6 pb-3 border-b-2 border-[#73BDF2]">{category}</h2>
                  <div className="space-y-4">
                    {categoryItems.map((item) => (
                      <div key={item.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-300">
                        <button
                          onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                          className="w-full flex items-center justify-between p-6 bg-gray-50 hover:bg-gray-100 transition-colors duration-300"
                        >
                          <span className="text-lg font-semibold text-left text-black">{item.question}</span>
                          <ChevronDown
                            size={24}
                            className={`flex-shrink-0 ml-4 text-[#73BDF2] transition-transform duration-300 ${
                              expandedId === item.id ? 'transform rotate-180' : ''
                            }`}
                          />
                        </button>
                        {expandedId === item.id && (
                          <div className="p-6 bg-white border-t border-gray-200">
                            <p className="text-gray-700 leading-relaxed">{item.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Didn't find your answer?</h2>
            <p className="text-gray-600 mb-8">
              Our support team is here to help. Get in touch with us for any questions or special requests.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:support@moreflexaviation.com"
                className="inline-block bg-[#73BDF2] text-white px-8 py-3 rounded hover:bg-[#5ba8e0] transition-colors duration-300 font-semibold"
              >
                Email Support
              </a>
              <a
                href="https://wa.me/254700000000"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-green-500 text-white px-8 py-3 rounded hover:bg-green-600 transition-colors duration-300 font-semibold"
              >
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8">Related Resources</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-bold text-lg mb-2">Contact Us</h3>
                <p className="text-gray-600 mb-4">Need immediate assistance? Our support team is available to help.</p>
                <a href="/contact" className="text-[#73BDF2] hover:text-[#5ba8e0] font-semibold">
                  Get in touch →
                </a>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-bold text-lg mb-2">Our Services</h3>
                <p className="text-gray-600 mb-4">Explore all the premium services we offer at JKIA.</p>
                <a href="/services" className="text-[#73BDF2] hover:text-[#5ba8e0] font-semibold">
                  View services →
                </a>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                <h3 className="font-bold text-lg mb-2">Industry Standards</h3>
                <p className="text-gray-600 mb-4">Learn about our compliance with IATA and international standards.</p>
                <a href="/resources" className="text-[#73BDF2] hover:text-[#5ba8e0] font-semibold">
                  Industry resources →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default FAQ;
